import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// THE CLUB'S MEMBER LIST — the browser half of `GET /members/roster`.
//
// Who is on this club's roster (`tenant_players`), which is not the portal's
// player list: that is `players`, shared by every club. Approving a join
// request puts somebody HERE, and this is where a club sees that it did.
//
// NO CLUB TO NAME, as with the join queue beside it: `apiFetch` carries the
// organization header and the server filters by the scope it resolves.
// ─────────────────────────────────────────────────────────────────────

export interface ClubMember {
  /** The player's LEGACY id — the id every list in the portal matches on. */
  id: string;
  name: string;
  /** When their roster row was born. Display only (DOMAIN_RULES §4). */
  since: string;
}

export type ClubMembersOutcome =
  | { status: 'ok'; members: ClubMember[] }
  /** Signed out, or no read scope here. The caller decides what to say. */
  | { status: 'refused' }
  /**
   * The server answered, and a row was not the contract. The WHOLE list fails,
   * for the join queue's reason (#735 review): a member silently dropped from
   * a list a club counts is worse than a list that says it is broken.
   */
  | { status: 'protocol' }
  | { status: 'unreachable' };

function readMember(raw: unknown): ClubMember | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  if (typeof r.id !== 'string' || r.id === '') return null;
  if (typeof r.name !== 'string') return null;
  if (typeof r.since !== 'string' || !Number.isFinite(Date.parse(r.since))) return null;
  return { id: r.id, name: r.name, since: r.since };
}

export async function listClubMembers(): Promise<ClubMembersOutcome> {
  let res: Response;
  try {
    res = await apiFetch('/members/roster');
  } catch {
    return { status: 'unreachable' };
  }
  if (res.status === 401 || res.status === 403) return { status: 'refused' };
  if (!res.ok) return { status: 'unreachable' };

  const body = (await res.json().catch(() => null)) as { members?: unknown } | null;
  if (!Array.isArray(body?.members)) return { status: 'protocol' };
  const parsed = body.members.map(readMember);
  if (parsed.some((m) => m === null)) return { status: 'protocol' };
  return { status: 'ok', members: parsed as ClubMember[] };
}
